import type { ElementType } from "react";
import { Link } from "react-router-dom";
import { Timestamp } from "@/components/ui/timestamp";
import { ScenarioCriticalityBadge } from "@/components/experience/scenario-criticality-badge";
import { PersonaRow } from "@/components/experience/persona-row";
import { cn } from "@/lib/utils";
import type { ScenarioRowProps } from "./scenario-row-types";
import {
  deriveBindingState,
  sortBindingsByUrgency,
  BindingStateLabel,
} from "./scenario-binding-state";

export function ScenarioVariantFull({
  scenario,
  effectiveHref,
  onSelect,
  projectId,
  actions,
  subtitle,
  headingLevel: Heading = "h3",
  className,
}: Pick<
  ScenarioRowProps,
  "scenario" | "onSelect" | "projectId" | "actions" | "subtitle" | "className"
> & {
  effectiveHref: string | null;
  headingLevel?: ElementType;
}) {
  const verdicts = scenario.personaVerdicts ?? [];
  const displayDate = scenario.lastWalkthroughAt ?? scenario.createdAt;
  const dek = subtitle ?? scenario.description;

  return (
    <li
      className={cn(
        "group px-4 py-3.5 transition-colors hover:bg-muted/15",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <Heading className="min-w-0 flex-1 line-clamp-2 text-base font-semibold sm:line-clamp-1">
          {effectiveHref ? (
            <Link
              to={effectiveHref}
              onClick={(e) => {
                if (
                  onSelect &&
                  !e.metaKey &&
                  !e.ctrlKey &&
                  !e.shiftKey &&
                  !e.altKey &&
                  e.button === 0
                ) {
                  e.preventDefault();
                  onSelect(scenario.id);
                }
              }}
              className="transition-colors group-hover:text-primary"
            >
              {scenario.name}
            </Link>
          ) : onSelect ? (
            <button
              type="button"
              onClick={() => onSelect(scenario.id)}
              className="cursor-pointer text-left transition-colors group-hover:text-primary"
            >
              {scenario.name}
            </button>
          ) : (
            <span>{scenario.name}</span>
          )}
        </Heading>

        <div className="flex shrink-0 items-center gap-3">
          {displayDate && (
            <Timestamp
              date={displayDate}
              className="w-16 text-right text-xs tabular-nums text-muted-foreground"
            />
          )}

          {actions}
        </div>
      </div>

      {dek && (
        <p
          className="mt-0.5 max-w-[80ch] line-clamp-2 text-sm text-muted-foreground"
          title={typeof dek === "string" ? dek : undefined}
        >
          {dek}
        </p>
      )}

      {(scenario.taskCriticality || scenario.tension) && (
        <div className="mt-2 flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          {scenario.taskCriticality && (
            <ScenarioCriticalityBadge criticality={scenario.taskCriticality} />
          )}
          {scenario.tension && (
            <p
              className="min-w-0 text-sm text-muted-foreground"
              title={scenario.tension}
            >
              Tension:{" "}
              <span className="text-foreground">{scenario.tension}</span>
            </p>
          )}
        </div>
      )}

      {verdicts.length > 0 ? (
        <ul className="mt-2 flex flex-col items-start gap-y-1">
          {sortBindingsByUrgency(verdicts).map((pv) => {
            const state = deriveBindingState(pv);
            const runsText =
              state.kind === "unrun"
                ? null
                : `${state.runCount} ${state.runCount === 1 ? "run" : "runs"}`;

            return (
              <li key={pv.personaId}>
                <PersonaRow
                  variant="pill"
                  persona={{
                    id: pv.personaId,
                    name: pv.personaName,
                  }}
                  size="xs"
                  href={
                    projectId
                      ? `/projects/${projectId}/scenarios/${scenario.id}#persona-${pv.personaId}`
                      : undefined
                  }
                  badge={
                    <div className="flex items-center gap-2">
                      {runsText && (
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {runsText}
                        </span>
                      )}
                      <BindingStateLabel state={state} />
                    </div>
                  }
                />
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          No personas cast yet.
        </p>
      )}
    </li>
  );
}
